import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button } from '@mlm/ui';
import { getMerchantMe, logout } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  approved: 'default',
  pending: 'secondary',
  rejected: 'destructive',
  suspended: 'destructive',
};

export function Layout() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { data } = useQuery({ queryKey: ['merchant-me'], queryFn: getMerchantMe });
  const status = data?.merchant.status;
  const approved = status === 'approved';

  const navItems: Array<{ to: string; label: string; gated: boolean }> = [
    { to: '/', label: 'Dashboard', gated: false },
    { to: '/products', label: 'Products', gated: true },
    { to: '/coupons', label: 'Coupons', gated: true },
    { to: '/variation-types', label: 'Variation Types', gated: true },
    { to: '/orders', label: 'Orders', gated: true },
    { to: '/feedback', label: 'Featured Feedback', gated: true },
    { to: '/messages', label: 'Messages', gated: true },
    { to: '/wallet', label: 'Wallet', gated: true },
    { to: '/payouts', label: 'Payouts', gated: true },
    { to: '/profile', label: 'Profile', gated: false },
  ];

  async function onLogout() {
    await logout();
    navigate('/login');
  }

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-56 shrink-0 border-r flex flex-col">
        <div className="px-4 py-4 border-b">
          <div className="font-semibold">{data?.merchant.businessName ?? 'Merchant'}</div>
          {status && (
            <Badge variant={STATUS_VARIANT[status] ?? 'outline'} className="mt-1 capitalize">
              {status}
            </Badge>
          )}
        </div>
        <nav className="flex-1 p-2 space-y-1">
          {navItems.map((item) => {
            const disabled = item.gated && !approved;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  [
                    'block rounded px-3 py-2 text-sm',
                    disabled ? 'pointer-events-none opacity-40' : 'hover:bg-accent',
                    isActive ? 'bg-accent font-medium' : '',
                  ].join(' ')
                }
              >
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t text-xs text-muted-foreground">
          <div className="truncate mb-2">{user?.email}</div>
          <Button variant="outline" size="sm" className="w-full" onClick={onLogout}>
            Sign out
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto p-6">
        <Outlet />
      </main>
    </div>
  );
}
